import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useUnifiedDashboard } from "../contexts/UnifiedDashboardContext";
import LoadingSpinner from "../components/LoadingSpinner";
import ScrollableTabs from "../components/ScrollableTabs";
import { Calendar, Trophy, TrendingUp, BarChart3, UserCheck, X, ChevronDown, ChevronRight, Target, PieChart, History } from "lucide-react";
import TrainingSessions from "../components/lnd/TrainingSessions";
import ParticipationRecords from "../components/lnd/ParticipationRecords";
import CPDRecords from "../components/lnd/CPDRecords";
import MandatoryCompliance from "../components/lnd/MandatoryCompliance";
import CategoryOverview from "../components/lnd/CategoryOverview";
import StaffJourney from "../components/lnd/StaffJourney";
import TrainingHoursAggregate from "../components/TrainingHoursAggregate";
import OfficialTrainingReport from "../components/OfficialTrainingReport";
import TrainingStatCards from "../components/lnd/TrainingStatCards";
import SortableHeader, { usePagedSortableData, TablePager } from "../components/SortableTable";
import { formatDateShort } from "../utils/dateFormat";
import { officialPeriodStart, officialPeriodEnd, officialPeriodRangeLabel, loadLdSettings } from "../utils/dateRange";
import { API_BASE_URL } from "../utils/api";
import { computeTrainingStats } from "../utils/trainingStats";

// Official training year (configured start → period end, Settings → L&D
// Training) — the shared default for every L&D date filter, so this tab and
// the detail tabs all cover the same period.

const LDDashboard = () => {
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  // The tabs wait for the configured training-year start / target hours: the
  // detail tabs initialise their default date ranges synchronously at mount,
  // so the settings cache must be primed before any of them render.
  const [settingsReady, setSettingsReady] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['Active']);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSponsorship, setSelectedSponsorship] = useState([]);
  // Set when the compliance table's "Learning journey" shortcut jumps to the
  // Staff Journey tab with that person already selected.
  const [journeyStaffId, setJourneyStaffId] = useState(null);

  // Live staff qualifications data
  const [qualificationsData, setQualificationsData] = useState([]);
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [sponsorshipAvailable, setSponsorshipAvailable] = useState(true);

  // Training data for official report
  const [trainingData, setTrainingData] = useState({ attendance: [], cpdRecords: [] });
  const [loadingTraining, setLoadingTraining] = useState(false);
  // When the rows above were last fetched — the Training Hours tab prints it.
  const [trainingLoadedAt, setTrainingLoadedAt] = useState(null);
  const [trainingStartDate, setTrainingStartDate] = useState(officialPeriodStart);
  const [trainingEndDate, setTrainingEndDate] = useState(officialPeriodEnd);

  const { isAuthenticated } = useAuth();
  const { switchDashboard } = useUnifiedDashboard();

  // Set the current dashboard in context
  useEffect(() => {
    switchDashboard("ld");
  }, [switchDashboard]);

  // Headline figures for the cards above the Official Training Report. The
  // aggregation itself lives in utils/trainingStats.js — the Training Hours tab
  // renders the same three numbers from the same call, so they cannot drift
  // apart.
  const liveStats = React.useMemo(
    () => computeTrainingStats(
      trainingData.attendance,
      trainingData.cpdRecords,
      trainingStartDate,
      trainingEndDate
    ),
    [trainingData, trainingStartDate, trainingEndDate]
  );

  // Filtered staff-qualifications rows, hoisted to component scope so the
  // sortable-header hook can run unconditionally (mirrors the filter used by
  // the Staff List table). Sort is applied to the top-level employee rows only.
  const filteredEmployees = React.useMemo(() => qualificationsData.filter(emp => {
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(emp.status)) return false;
    if (selectedPositions.length > 0 && !selectedPositions.includes(emp.position)) return false;
    if (selectedQualifications.length > 0) {
      const empQuals = (emp.qualifications || 'None').split(';').map(q => q.trim());
      if (!selectedQualifications.some(q => empQuals.includes(q))) return false;
    }
    if (selectedSponsorship.length > 0 && !selectedSponsorship.includes(emp.firmSponsorship || 'No')) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (
        !(emp.name || '').toLowerCase().includes(q) &&
        !(emp.position || '').toLowerCase().includes(q) &&
        !(emp.department || '').toLowerCase().includes(q) &&
        !(emp.qualifications || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  }), [qualificationsData, selectedStatuses, selectedPositions, selectedQualifications, selectedSponsorship, searchTerm]);
  const {
    rows: employeePage,
    sortConfig: qualSortConfig,
    requestSort: requestQualSort,
    page: qualPage,
    setPage: setQualPage,
    total: qualTotal,
  } = usePagedSortableData(filteredEmployees);

  // Mark the dashboard ready on mount
  useEffect(() => {
    if (isAuthenticated()) {
      setDataLoaded(true);
    }
  }, [isAuthenticated]);

  // Prime the L&D settings cache, then re-seed the Overview date filter with
  // the configured period (nobody can have touched the inputs yet — the page
  // is still behind the loading gate).
  useEffect(() => {
    let active = true;
    loadLdSettings().then(() => {
      if (!active) return;
      setTrainingStartDate(officialPeriodStart());
      setTrainingEndDate(officialPeriodEnd());
      setSettingsReady(true);
    });
    return () => { active = false; };
  }, []);

  // Fetch the attendance + External CPD rows behind both the Overview cards and
  // the Training Hours tab. One fetch for the page — the tab must not request
  // the same two endpoints again on its own.
  const fetchTrainingData = React.useCallback(async () => {
    if (!isAuthenticated()) return;

    setLoadingTraining(true);
    setError(null);
    try {
      const [attendanceRes, cpdRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ld/attendance`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(`${API_BASE_URL}/api/ld/cpd-records`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        })
      ]);

      if (!attendanceRes.ok || !cpdRes.ok) {
        setError(attendanceRes.status === 403 || cpdRes.status === 403
          ? 'You do not have permission to view Professional Development data — ask an administrator for the ld:view permission.'
          : 'Training data could not be loaded. The figures below may be incomplete — try refreshing the page.');
      }

      const attendance = attendanceRes.ok ? (await attendanceRes.json()).data || [] : [];
      const cpdRecords = cpdRes.ok ? (await cpdRes.json()).data || [] : [];

      setTrainingData({ attendance, cpdRecords });
      setTrainingLoadedAt(new Date());
    } catch (error) {
      console.error('Error fetching L&D training data:', error);
      setError('Training data could not be loaded — the server may be unreachable.');
    } finally {
      setLoadingTraining(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchTrainingData();
  }, [fetchTrainingData]);

  // Fetch live staff qualifications
  useEffect(() => {
    const fetchQualifications = async () => {
      if (!isAuthenticated()) return;
      setLoadingQualifications(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/ld/staff-qualifications`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          setError(res.status === 403
            ? 'You do not have permission to view Professional Development data — ask an administrator for the ld:view permission.'
            : 'Staff qualifications could not be loaded — try refreshing the page.');
        }
        if (res.ok) {
          const result = await res.json();
          // Display adapter: sponsorshipAwarded (boolean) → 'Yes'/'No' badge
          // value; dates stay ISO for sorting and are formatted in the cells.
          setQualificationsData((result.data || []).map(emp => ({
            ...emp,
            firmSponsorship: emp.sponsorshipAwarded ? 'Yes' : 'No',
          })));
          setSponsorshipAvailable(result.sponsorshipAvailable !== false);
        }
      } catch (err) {
        console.error('Error fetching staff qualifications:', err);
        setError('Staff qualifications could not be loaded — the server may be unreachable.');
      } finally {
        setLoadingQualifications(false);
      }
    };
    fetchQualifications();
  }, [isAuthenticated]);

  // Tab order: Mandatory Compliance sits next to Training
  // Hours. Per-record viewing (attendance / absence reasons /
  // training feedback) is one Participation Records tab with shared
  // filters — session-centric viewing lives on the Training
  // Sessions tab.
  const tabs = [
    { id: "overview", name: "Overview", icon: TrendingUp, description: "L&D Dashboard Summary" },
    { id: "trainingHoursAggregate", name: "Training Hours", icon: BarChart3, description: "Aggregate training hours from all sources" },
    { id: "byCategory", name: "By Category", icon: PieChart, description: "Training hours by training category" },
    { id: "compliance", name: "Mandatory Compliance", icon: Target, description: "Internal mandatory hours vs the yearly target" },
    { id: "staffJourney", name: "Staff Journey", icon: History, description: "One staff member's learning history and timeline" },
    { id: "trainingSessions", name: "Training Sessions", icon: Calendar, description: "Internal training sessions" },
    { id: "cpdRecords", name: "External CPD", icon: Trophy, description: "External training certificates" },
    { id: "qualifications", name: "Professional Qualifications", icon: Trophy, description: "Staff qualifications by year" },
    { id: "participation", name: "Participation Records", icon: UserCheck, description: "Attendance, absences and feedback across sessions" },
  ];

  // Show loading spinner while checking for stored data
  if (!dataLoaded || !settingsReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading Professional Development Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Secondary Navigation (Tabs) — same ScrollableTabs component as the QM
          dashboard (docs/dashboard-ui.md: one tab bar for both dashboards) */}
      <div className="shrink-0">
        <ScrollableTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-slate-50 p-6">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">Notice</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <button onClick={() => setError(null)} className="mt-2 text-xs text-red-600 hover:text-red-800 underline">Dismiss</button>
          </div>
        )}

        {/* Tab Content — no page/tab titles here: the tab name already says
            where we are (header rules in docs/dashboard-ui.md) */}
        <div className="min-h-[600px]">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Key Metrics Cards — all staff with records in the period,
                  including non-audit grades and former staff; the Official
                  Training Report below counts audit grades only, so the two
                  sets of numbers legitimately differ (noted on the cards).
                  Same component as the Training Hours tab,
                  so the two tabs cannot look like different reports. */}
              <TrainingStatCards stats={liveStats} />

              {/* Official Training Report with Live Data — internal attendance
                  and external CPD each stand on their own (a period with only
                  CPD certificates still has a report worth showing). */}
              {!loadingTraining && (trainingData.attendance.length > 0 || trainingData.cpdRecords.length > 0) && (
                <div className="space-y-4">
                  {/* Date Filter for Training Report */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <h4 className="text-sm font-semibold text-gray-700">Official Training Report Period:</h4>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">From:</label>
                        <input
                          type="date"
                          value={trainingStartDate}
                          onChange={(e) => setTrainingStartDate(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">To:</label>
                        <input
                          type="date"
                          value={trainingEndDate}
                          onChange={(e) => setTrainingEndDate(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <button
                        onClick={() => {
                          setTrainingStartDate(officialPeriodStart());
                          setTrainingEndDate(officialPeriodEnd());
                        }}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Reset to Default ({officialPeriodRangeLabel()})
                      </button>
                    </div>
                  </div>

                  {/* Official Report */}
                  <OfficialTrainingReport
                    attendance={trainingData.attendance}
                    cpdRecords={trainingData.cpdRecords}
                    startDate={trainingStartDate}
                    endDate={trainingEndDate}
                  />
                </div>
              )}

              {loadingTraining && (
                <div className="bg-blue-50 rounded-lg border border-blue-200 p-6 text-center">
                  <LoadingSpinner />
                  <p className="mt-2 text-sm text-blue-700">Loading training data for official report...</p>
                </div>
              )}
            </div>
          )}

          {/* Professional Qualifications Tab */}
          {activeTab === "qualifications" && (
            <div className="space-y-6">
              {loadingQualifications && (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                  <p className="ml-3 text-slate-600">Loading staff qualifications...</p>
                </div>
              )}

              {!loadingQualifications && !sponsorshipAvailable && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
                  <strong>Training Agreements data unavailable.</strong> Training agreement details could not be loaded.
                  Qualifications data is shown, but all sponsorship fields reflect &ldquo;No&rdquo;.
                </div>
              )}

              {!loadingQualifications && (() => {
                const activeEmployees = qualificationsData.filter(emp => emp.status === 'Active');
                const qualCounts = {};
                activeEmployees.forEach(emp => {
                  const quals = (emp.qualifications || 'None').split(';').map(q => q.trim());
                  quals.forEach(q => { qualCounts[q] = (qualCounts[q] || 0) + 1; });
                });
                const total = activeEmployees.length;
                const sortedQuals = Object.entries(qualCounts).sort((a, b) => b[1] - a[1]);
                const colorMap = {
                  0: '#ef4444', 1: '#f97316', 2: '#f59e0b', 3: '#eab308',
                  4: '#84cc16', 5: '#22c55e', 6: '#10b981', 7: '#14b8a6',
                  8: '#06b6d4', 9: '#0ea5e9', 10: '#3b82f6', 11: '#6366f1',
                  12: '#8b5cf6', 13: '#a855f7', 14: '#d946ef', 15: '#ec4899'
                };
                const colors = [
                  'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
                  'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
                  'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
                  'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'
                ];

                // Derived filter options
                const positionOptions = [...new Set(qualificationsData.map(e => e.position).filter(Boolean))].sort();
                const qualOptions = [...new Set(
                  qualificationsData.flatMap(e => (e.qualifications || 'None').split(';').map(q => q.trim()))
                )].sort();
                const statusOptions = [...new Set(qualificationsData.map(e => e.status).filter(Boolean))].sort();

                // The current page of the filtered + sorted list (both applied at
                // component scope); `qualTotal` is the full filtered count.
                const filteredData = employeePage;

                return (
                  <>
                    {/* Pie Chart – Active employees */}
                    {total > 0 && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Professional Qualifications Distribution</h3>
                        <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50">
                          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">Active Employees – Audit &amp; Assurance</h3>
                          <div className="grid md:grid-cols-[1fr,400px] gap-8 items-center">
                            <div className="flex justify-center">
                              <div className="w-80 h-80 rounded-full overflow-hidden shadow-lg" style={{
                                background: `conic-gradient(${sortedQuals.map(([, count], idx) => {
                                  const startPct = sortedQuals.slice(0, idx).reduce((s, [, c]) => s + (c / total * 100), 0);
                                  const endPct = startPct + (count / total * 100);
                                  return `${colorMap[idx % 16] || '#6b7280'} ${startPct}% ${endPct}%`;
                                }).join(', ')})`
                              }} />
                            </div>
                            <div>
                              <div className="space-y-1 text-sm max-h-96 overflow-y-auto pr-2">
                                {sortedQuals.map(([qual, count], idx) => (
                                  <div key={qual} className="flex items-center justify-between gap-3 py-1">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className={`w-4 h-4 rounded flex-shrink-0 ${colors[idx % colors.length]}`} />
                                      <span className="text-gray-700 truncate" title={qual}>{qual}</span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className="font-semibold text-gray-900">{((count / total) * 100).toFixed(0)}%</span>
                                      <span className="text-gray-500">({count})</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-6 pt-4 border-t border-green-200">
                                <span className="text-base font-semibold text-gray-900">Total: {total} active employees</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Detailed Table */}
                    <details className="bg-white rounded-lg shadow-sm border border-gray-200" open>
                      <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 font-medium text-gray-900">
                        Staff List — Audit &amp; Assurance ({activeEmployees.length} Active • {qualificationsData.length} Total)
                      </summary>
                      <div className="px-6 pb-6">
                        {/* Search */}
                        <div className="mt-4 mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search Employees
                          </label>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, position, department, or qualifications..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          {searchTerm && (
                            <button onClick={() => setSearchTerm("")} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                              <X className="h-3 w-3 mr-1" />Clear Search
                            </button>
                          )}
                        </div>

                        {/* Filters */}
                        <div className="mt-4 mb-4 grid gap-4 md:grid-cols-4">
                          {/* Position */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Filter by Designation
                            </label>
                            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                              {positionOptions.map(pos => (
                                <label key={pos} className="flex items-center py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                                  <input type="checkbox" checked={selectedPositions.includes(pos)}
                                    onChange={e => setSelectedPositions(e.target.checked ? [...selectedPositions, pos] : selectedPositions.filter(p => p !== pos))}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{pos}</span>
                                </label>
                              ))}
                            </div>
                            {selectedPositions.length > 0 && (
                              <button onClick={() => setSelectedPositions([])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                <X className="h-3 w-3 mr-1" />Clear ({selectedPositions.length})
                              </button>
                            )}
                          </div>

                          {/* Qualifications */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Filter by Qualifications
                            </label>
                            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                              {qualOptions.map(qual => (
                                <label key={qual} className="flex items-center py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                                  <input type="checkbox" checked={selectedQualifications.includes(qual)}
                                    onChange={e => setSelectedQualifications(e.target.checked ? [...selectedQualifications, qual] : selectedQualifications.filter(q => q !== qual))}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{qual}</span>
                                </label>
                              ))}
                            </div>
                            {selectedQualifications.length > 0 && (
                              <button onClick={() => setSelectedQualifications([])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                <X className="h-3 w-3 mr-1" />Clear ({selectedQualifications.length})
                              </button>
                            )}
                          </div>

                          {/* Sponsorship */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Firm's Sponsorship
                            </label>
                            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                              {['Yes', 'No'].map(s => (
                                <label key={s} className="flex items-center py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                                  <input type="checkbox" checked={selectedSponsorship.includes(s)}
                                    onChange={e => setSelectedSponsorship(e.target.checked ? [...selectedSponsorship, s] : selectedSponsorship.filter(x => x !== s))}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{s}</span>
                                </label>
                              ))}
                            </div>
                            {selectedSponsorship.length > 0 && (
                              <button onClick={() => setSelectedSponsorship([])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                <X className="h-3 w-3 mr-1" />Clear ({selectedSponsorship.length})
                              </button>
                            )}
                          </div>

                          {/* Status */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Employee Status
                            </label>
                            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                              {statusOptions.map(s => (
                                <label key={s} className="flex items-center py-1 hover:bg-gray-100 px-2 rounded cursor-pointer">
                                  <input type="checkbox" checked={selectedStatuses.includes(s)}
                                    onChange={e => setSelectedStatuses(e.target.checked ? [...selectedStatuses, s] : selectedStatuses.filter(x => x !== s))}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                  <span className="ml-2 text-sm text-gray-700">{s}</span>
                                </label>
                              ))}
                            </div>
                            {selectedStatuses.length > 0 && (
                              <button onClick={() => setSelectedStatuses([])} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center">
                                <X className="h-3 w-3 mr-1" />Clear ({selectedStatuses.length})
                              </button>
                            )}
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">Showing {qualTotal} of {qualificationsData.length} employees</p>

                        <div className="table-scroll">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 w-8" />
                                <SortableHeader label="Name" sortKey="name" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                                <SortableHeader label="Designation" sortKey="position" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                                <SortableHeader label="Qualifications" sortKey="qualifications" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                                <SortableHeader label="Status" sortKey="status" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                                <SortableHeader label="Firm's Sponsorship" sortKey="firmSponsorship" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                                <SortableHeader label="Last Updated" sortKey="lastUpdated" sortConfig={qualSortConfig} onSort={requestQualSort} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredData.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No employees match the selected filters</td>
                                </tr>
                              ) : filteredData.map((employee) => {
                                // Keyed by name, not row index: sorting or
                                // filtering the table must not move the open
                                // agreement panel onto somebody else's row.
                                const rowKey = employee.name;
                                const isExpanded = expandedRows.has(rowKey);
                                const hasSponsorship = employee.firmSponsorship === 'Yes' && employee.sponsorshipDetails;
                                return (
                                  <React.Fragment key={rowKey}>
                                    <tr className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-600">
                                        {hasSponsorship && (
                                          <button onClick={() => {
                                            const next = new Set(expandedRows);
                                            isExpanded ? next.delete(rowKey) : next.add(rowKey);
                                            setExpandedRows(next);
                                          }} className="text-blue-600 hover:text-blue-800">
                                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                          </button>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900">{employee.name}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{employee.position || '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{employee.qualifications || 'None'}</td>
                                      <td className="px-4 py-3 text-sm">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                          employee.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                        }`}>{employee.status || '—'}</span>
                                      </td>
                                      <td className="px-4 py-3 text-sm">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                          employee.firmSponsorship === 'Yes' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                        }`}>{employee.firmSponsorship || 'No'}</span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-500">{employee.lastUpdated ? formatDateShort(employee.lastUpdated) : '—'}</td>
                                    </tr>
                                    {isExpanded && hasSponsorship && (
                                      <tr className="bg-blue-50">
                                        <td colSpan="7" className="px-4 py-4">
                                          <div className="ml-8">
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Training Agreement Details</h4>
                                            <table className="min-w-full text-sm">
                                              <thead>
                                                <tr className="border-b border-blue-200">
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Qualification</th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Agreement Date</th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Contracting Entity</th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Capped At</th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Signing Partner</th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Remarks</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {employee.sponsorshipDetails.map((d, di) => (
                                                  <tr key={di} className="border-b border-blue-100">
                                                    <td className="px-3 py-2 text-gray-900">{d.professionalQualification || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{d.trainingAgreementDate ? formatDateShort(d.trainingAgreementDate) : '—'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{d.contractingEntity || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{d.cappedAt ? `$${Number(d.cappedAt).toLocaleString()}` : '—'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{d.signingPartner || '—'}</td>
                                                    <td className="px-3 py-2 text-gray-500 italic break-words">{d.remarks || '—'}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <TablePager page={qualPage} setPage={setQualPage} total={qualTotal} />
                      </div>
                    </details>
                  </>
                );
              })()}
            </div>
          )}

          {/* Training Hours Aggregate Tab - Combined Attendance + CPD Records */}
          {activeTab === "trainingHoursAggregate" && (
            <TrainingHoursAggregate
              attendance={trainingData.attendance}
              cpdRecords={trainingData.cpdRecords}
              startDate={trainingStartDate}
              endDate={trainingEndDate}
              onStartDateChange={setTrainingStartDate}
              onEndDateChange={setTrainingEndDate}
              loading={loadingTraining}
              lastUpdated={trainingLoadedAt}
              onRefresh={fetchTrainingData}
            />
          )}

          {/* By Category Tab — same page-level rows and period as Training
              Hours, so the two tabs always cover the same fetch. */}
          {activeTab === "byCategory" && (
            <CategoryOverview
              attendance={trainingData.attendance}
              cpdRecords={trainingData.cpdRecords}
              startDate={trainingStartDate}
              endDate={trainingEndDate}
              onStartDateChange={setTrainingStartDate}
              onEndDateChange={setTrainingEndDate}
              loading={loadingTraining}
              lastUpdated={trainingLoadedAt}
              onRefresh={fetchTrainingData}
            />
          )}

          {/* Mandatory Compliance Tab */}
          {activeTab === "compliance" && (
            <MandatoryCompliance
              onViewJourney={(staffId) => {
                setJourneyStaffId(staffId);
                setActiveTab("staffJourney");
              }}
            />
          )}

          {/* Staff Journey Tab */}
          {activeTab === "staffJourney" && <StaffJourney initialStaffId={journeyStaffId} />}

          {/* Training Sessions Tab */}
          {activeTab === "trainingSessions" && <TrainingSessions />}

          {/* CPD Records Tab */}
          {activeTab === "cpdRecords" && <CPDRecords />}

          {/* Participation Records Tab — Attendance / Absence Reasons /
              Training Feedback as sub-tabs behind one set of shared filters.
              Attendance rows are the page-level fetch; the refresh button
              re-runs it alongside the tab's own absence/feedback requests. */}
          {activeTab === "participation" && (
            <ParticipationRecords
              attendance={trainingData.attendance}
              attendanceLoading={loadingTraining}
              onRefreshAttendance={fetchTrainingData}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default LDDashboard;
